import type { Immutable } from 'immer';
import type { AssetDefinitionExtraArgs } from './definitions.ts';
import type { AssetProperties } from './properties.ts';

export type RoomDeviceSlotProperties<A extends AssetDefinitionExtraArgs = AssetDefinitionExtraArgs> = Omit<AssetProperties<A>, 'stateFlags' | 'blockAddRemove' | 'blockModules'>;

export interface RoomDeviceProperties<A extends AssetDefinitionExtraArgs = AssetDefinitionExtraArgs> {
	/**
	 * Properties for individual slots.
	 * @default {}
	 */
	slotProperties?: Partial<Record<string, RoomDeviceSlotProperties<A>>>;

	/**
	 * Flags allowing to limit which item's state combinations are valid.
	 * Flags only operate within a single item, they do not affect other items.
	 */
	stateFlags?: {
		/**
		 * State flags this state gives.
		 * @default []
		 * @example
		 * ['strap']
		 */
		provides?: string[];

		/**
		 * Flags that this state requires.
		 *
		 * Format is a record of <`flag`, `description if not met`>.
		 * @default {}
		 * @example
		 * {
		 *     strap: 'This option requires a strap to be present.',
		 * }
		 */
		requires?: { [flag: string]: string; };
	};

	/**
	 * If set, then the state is marked as invalid. The value is user-visible description of the reason.
	 *
	 * This property is intended to be used inside `stateFlagCombinations`.
	 */
	invalid?: string;

	/**
	 * Prevents listed modules from being modified by anyone, including on oneself
	 * @default []
	 */
	blockModules?: string[];

	/**
	 * Prevents this slot from being entered or exited by anyone, including on oneself
	 * @default false
	 */
	blockSlotsEnterLeave?: string[];
}

export interface RoomDevicePropertiesResult {
	slotProperties: Partial<Record<string, Immutable<AssetProperties>[]>>;
	stateFlags: Set<string>;
	stateFlagsRequirements: Map<string, string>;
	invalid?: Set<string>;
	blockModules: Set<string>;
	blockSlotsEnterLeave: Set<string>;
}

export function CreateRoomDevicePropertiesResult(): RoomDevicePropertiesResult {
	return {
		slotProperties: {},
		stateFlags: new Set(),
		stateFlagsRequirements: new Map(),
		blockModules: new Set(),
		blockSlotsEnterLeave: new Set(),
	};
}

export function MergeRoomDeviceProperties<T extends RoomDevicePropertiesResult>(base: T, properties: Immutable<RoomDeviceProperties>): T {
	for (const [slot, slotProperties] of Object.entries(properties.slotProperties ?? {})) {
		if (slotProperties == null)
			continue;

		(base.slotProperties[slot] ??= []).push(slotProperties);
	}

	properties.stateFlags?.provides?.forEach((a) => base.stateFlags.add(a));
	// Merge required flags. The requirement reasons are simply concatenated (with a space between)
	for (const [flag, reason] of Object.entries(properties.stateFlags?.requires ?? {})) {
		const currentReason = base.stateFlagsRequirements.get(flag);
		base.stateFlagsRequirements.set(flag, (currentReason ? `${currentReason} ` : '') + reason);
	}
	if (properties.invalid != null) {
		base.invalid ??= new Set();
		base.invalid.add(properties.invalid);
	}

	properties.blockModules?.forEach((a) => base.blockModules.add(a));
	properties.blockSlotsEnterLeave?.forEach((a) => base.blockSlotsEnterLeave.add(a));

	return base;
}

export function GetPropertiesForSlot(deviceProperties: RoomDevicePropertiesResult, slot: string): Immutable<AssetProperties>[] {
	const result: Immutable<AssetProperties>[] = [];

	const slotProperties = deviceProperties.slotProperties[slot];
	if (slotProperties != null) {
		result.push(...slotProperties);
	}

	result.push({
		blockAddRemove: deviceProperties.blockSlotsEnterLeave.has(slot),
	});

	return result;
}
